/**
 * API Client Configuration
 *
 * Axios-based HTTP client for communicating with the Luminaria API.
 * Uses Supabase Auth for token management.
 */

import type { ApiErrorResponse } from '@luminaria/shared';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';

// Token getter, token refresher, and logout handler will be set by AuthContext
let getAccessToken: (() => Promise<string | null>) | null = null;
let refreshAccessToken: (() => Promise<string | null>) | null = null;
let logoutHandler: (() => void) | null = null;

// Prevent multiple concurrent token refreshes
let refreshPromise: Promise<string | null> | null = null;

/**
 * Set the token getter function (called by AuthContext)
 */
export function setTokenGetter(getter: () => Promise<string | null>) {
  getAccessToken = getter;
}

/**
 * Set the token refresher function (called by AuthContext)
 * This attempts to refresh the Supabase session and returns a new access token.
 */
export function setTokenRefresher(refresher: () => Promise<string | null>) {
  refreshAccessToken = refresher;
}

/**
 * Set the logout handler function (called by AuthContext)
 */
export function setLogoutHandler(handler: () => void) {
  logoutHandler = handler;
}

/**
 * Get the current access token
 */
async function getToken(): Promise<string | null> {
  if (getAccessToken) {
    return await getAccessToken();
  }
  return null;
}

/**
 * Main API client instance
 */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 second timeout (account linking/sync can take a while)
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor - attach Supabase access token
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // If Authorization header already set (e.g., manual override), don't overwrite
    if (config.headers?.Authorization) {
      return config;
    }

    if (getAccessToken && config.headers) {
      try {
        const token = await getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // Token fetch failed, continue without auth
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor - auto-unwrap data wrapper and handle errors
 */
apiClient.interceptors.response.use(
  (response) => {
    // Auto-unwrap the standard { success: true, data: {...} } wrapper
    // This eliminates the awkward data.data.something pattern
    if (response.data?.success === true && response.data?.data !== undefined) {
      response.data = response.data.data;
    }
    // If success is false, leave the full structure for error handling
    // If there's no wrapper at all, leave response as-is
    return response;
  },
  async (error: AxiosError) => {
    // Silently reject aborted requests - these occur during page refresh/navigation
    // when React Query cancels in-flight requests. Not a real error.
    if (axios.isCancel(error) || error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - attempt token refresh before logging out.
    // When the app returns from background, the JWT may be expired but Supabase
    // can still refresh it. We try refreshing once before giving up.
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    if (error.response?.status === 401 && originalRequest && !originalRequest._retried) {
      originalRequest._retried = true;

      if (refreshAccessToken) {
        try {
          // Deduplicate concurrent refresh attempts
          if (!refreshPromise) {
            refreshPromise = refreshAccessToken().finally(() => {
              refreshPromise = null;
            });
          }
          const newToken = await refreshPromise;

          if (newToken) {
            // Retry the original request with the fresh token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          }
        } catch {
          // Refresh failed - fall through to logout
        }
      }

      // Token refresh failed or unavailable - log out
      if (logoutHandler) {
        setTimeout(() => logoutHandler?.(), 0);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Error thrown by postEventStream for a non-2xx response; carries the API
 * envelope's code and the status so callers can treat it like an axios error.
 */
class EventStreamError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = 'EventStreamError';
  }
}

async function openEventStream(path: string, body: unknown, signal: AbortSignal | undefined, token: string | null) {
  return fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });
}

/**
 * POST a JSON body to a Server-Sent-Events endpoint and hand each `data:`
 * frame (parsed as JSON) to `onEvent`. EventSource can't POST, so this uses
 * fetch — with the same base URL, bearer token and one-shot 401 refresh as
 * the axios client. `onEvent` returns `'stop'` to stop reading early.
 * A non-2xx response rejects with EventStreamError carrying the API
 * envelope's message and code.
 */
export async function postEventStream<TEvent>(
  path: string,
  body: unknown,
  options: { signal?: AbortSignal; onEvent: (event: TEvent) => 'stop' | void }
): Promise<void> {
  let response = await openEventStream(path, body, options.signal, await getToken());

  if (response.status === 401 && refreshAccessToken) {
    const newToken = await refreshAccessToken().catch(() => null);
    if (newToken) {
      response = await openEventStream(path, body, options.signal, newToken);
    }
  }

  if (!response.ok) {
    let message = `HTTP ${response.status}: ${response.statusText}`;
    let code: string | undefined;
    try {
      const parsed = (await response.json()) as Partial<ApiErrorResponse>;
      if (parsed.error?.message) message = parsed.error.message;
      code = parsed.error?.code;
    } catch {
      // Not a JSON envelope — keep the status line
    }
    throw new EventStreamError(message, response.status, code);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Response body is null');
  }
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) return;

    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? ''; // keep the incomplete frame

    for (const frame of frames) {
      if (!frame.startsWith('data: ')) continue;
      let event: TEvent;
      try {
        event = JSON.parse(frame.slice(6)) as TEvent;
      } catch (parseError) {
        console.error('Failed to parse SSE message:', parseError);
        continue;
      }
      if (options.onEvent(event) === 'stop') {
        await reader.cancel();
        return;
      }
    }
  }
}
